import { RoleId, RoleName } from '@common/constants';
import type { UserEntry } from '@common/types/users';
import SearchIcon from '@mui/icons-material/Search';
import {
    Button,
    Chip,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DataTable, { type Column } from '@/components/DataTable';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { useUsers } from '@/queries/users';
import useIdentityStore from '@/stores/useIdentityStore';
import { canManageFirmUsers } from '@/utils/user';
import AccountAccessDialog from './AccountAccessDialog';
import CreateAccountDialog from './CreateAccountDialog';
import DeleteUserDialog from './DeleteUserDialog';
import ResendAccountDetailsDialog from './ResendAccountDetailsDialog';

const ROLES_TYPES = [
    'All',
    RoleName.LAWYER,
    RoleName.CLERK,
    RoleName.NOTARY,
    'Organizational Admin',
] as const;

const FILTER_ROLE_IDS: Record<string, RoleId> = {
    [RoleName.LAWYER]: RoleId.LAWYER,
    [RoleName.CLERK]: RoleId.CLERK,
    [RoleName.NOTARY]: RoleId.NOTARY,
    'Organizational Admin': RoleId.ORGANIZATIONAL_ADMIN,
};

const FORM_ITEM_STYLES = {
    backgroundColor: '#F4F6F8',
    color: '#7B7D80',
};

export default function Users() {
    const { activeLawFirmId } = useLawFirmContext();
    const loggedInUser = useIdentityStore((state) => state.user);
    const canManageUsers = canManageFirmUsers(loggedInUser, activeLawFirmId);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [accountAccessDialogMode, setAccountAccessDialogMode] = useState<
        'disable' | 'enable' | null
    >(null);

    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

    const [filterConfig, setFilterConfig] = useState<{
        role: (typeof ROLES_TYPES)[number];
        searchKey: string;
    }>({
        searchKey: '',
        role: 'All',
    });

    const { data: users = [], isLoading: isUsersLoading } = useUsers({
        lawFirmId: activeLawFirmId,
    });

    const filteredUsers = useMemo(() => {
        return users.filter((user) => {
            const isRoleMatched =
                filterConfig.role === 'All' ||
                user.roles?.some((role) => role.roleId === FILTER_ROLE_IDS[filterConfig.role]);
            const isSearchKeyMatched =
                filterConfig.searchKey === '' ||
                user.fullName.toLowerCase().includes(filterConfig.searchKey.toLowerCase()) ||
                user.email.toLowerCase().includes(filterConfig.searchKey.toLowerCase());
            return isRoleMatched && isSearchKeyMatched;
        });
    }, [users, filterConfig]);

    const selectedUser = useMemo(() => {
        if (selectedUserIds.length === 0) return null;
        return filteredUsers.find((user) => user.userId === Number(selectedUserIds[0])) ?? null;
    }, [filteredUsers, selectedUserIds]);
    // So an admin can't lock themselves out
    const isSelfSelected = selectedUser?.userId === loggedInUser?.userId;

    const { data: activeLawFirm } = useLawFirm(activeLawFirmId);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const columns: Column<UserEntry>[] = [
        {
            id: 'fullName',
            label: 'User',
            _render: (user) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Link style={{ fontWeight: '500' }} to={`/user/${user.userId}`}>
                        {user.fullName}
                    </Link>
                    {!user.accountEnabled && (
                        <Chip label="Disabled" size="small" color="warning" variant="outlined" />
                    )}
                </Stack>
            ),
            _isSortable: false,
        },
        {
            id: 'roles',
            label: 'Roles',
            _render: (user) => {
                const roleNames = user.roles
                    ?.filter((role) => role.roleId !== RoleId.BASIC)
                    .map((role) => role.name);
                return roleNames?.join(', ') ?? '';
            },
            _isSortable: false,
        },
        { id: 'email', label: 'Email', _isSortable: true },
        { id: 'phoneNumber', label: 'Phone number', _isSortable: true },
        {
            id: 'lawyerId',
            label: 'Assigned lawyer',
            _render: (user) => {
                const assignedUser = users.find((u) => u.userId === user.lawyerId);
                return assignedUser ? (
                    <Link to={`/user/${assignedUser.userId}`}>{assignedUser.fullName}</Link>
                ) : null;
            },
            _isSortable: false,
        },
    ];

    const handleFilterConfigChange = (ev: { target: { value: string } }, key: string) => {
        setFilterConfig((prev) => ({
            ...prev,
            [key]: ev.target.value,
        }));
    };

    return (
        <PageWrapper
            title="Users"
            actions={
                <>
                    {canManageUsers && selectedUser && !isSelfSelected && (
                        <Button
                            variant="outlined"
                            color="error"
                            sx={{ color: 'error.main' }}
                            onClick={() => setIsDeleteDialogOpen(true)}
                        >
                            Delete User
                        </Button>
                    )}
                    {canManageUsers && selectedUser && !isSelfSelected && (
                        <Button
                            variant="outlined"
                            color={selectedUser.accountEnabled ? 'error' : 'primary'}
                            sx={{ color: selectedUser.accountEnabled ? 'error.main' : 'primary' }}
                            onClick={() =>
                                setAccountAccessDialogMode(
                                    selectedUser.accountEnabled ? 'disable' : 'enable',
                                )
                            }
                        >
                            {selectedUser.accountEnabled ? 'Disable Account' : 'Enable Account'}
                        </Button>
                    )}
                    {canManageUsers && selectedUser && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setIsResendDialogOpen(true)}
                        >
                            Resend Account Details
                        </Button>
                    )}
                    {canManageUsers && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setIsCreateDialogOpen(true)}
                        >
                            Create User
                        </Button>
                    )}
                </>
            }
        >
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                    <TextField
                        value={filterConfig?.searchKey}
                        onChange={(ev) => handleFilterConfigChange(ev, 'searchKey')}
                        sx={{ ...FORM_ITEM_STYLES, width: '400px' }}
                        placeholder="Search users by name or email"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    ></TextField>
                    <FormControl
                        sx={{
                            ...FORM_ITEM_STYLES,
                            width: 'fit-content',
                            minWidth: '120px',
                        }}
                    >
                        <InputLabel id="role">Role</InputLabel>
                        <Select
                            labelId="role"
                            label="Role"
                            value={filterConfig.role}
                            onChange={(ev) => handleFilterConfigChange(ev, 'role')}
                        >
                            {ROLES_TYPES.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Stack>
            <DataTable
                generateKeyFrom={'userId'}
                isLoading={isUsersLoading}
                isSelectable={canManageUsers}
                selectionMode="single"
                hideSelectAll
                columns={columns}
                rows={filteredUsers}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={(_ev, newPage) => setPage(newPage)}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                selected={selectedUserIds}
                setSelected={setSelectedUserIds}
            />
            {isCreateDialogOpen && (
                <CreateAccountDialog
                    lawFirmId={activeLawFirmId as number}
                    lawFirmName={activeLawFirm?.companyName}
                    onClose={() => setIsCreateDialogOpen(false)}
                />
            )}
            {isResendDialogOpen && selectedUser && (
                <ResendAccountDetailsDialog
                    email={selectedUser.email}
                    onClose={() => {
                        setIsResendDialogOpen(false);
                        setSelectedUserIds([]);
                    }}
                />
            )}
            {accountAccessDialogMode && selectedUser && (
                <AccountAccessDialog
                    email={selectedUser.email}
                    mode={accountAccessDialogMode}
                    onClose={() => {
                        setAccountAccessDialogMode(null);
                        setSelectedUserIds([]);
                    }}
                />
            )}
            {isDeleteDialogOpen && selectedUser && (
                <DeleteUserDialog
                    userId={selectedUser.userId}
                    email={selectedUser.email}
                    onClose={() => {
                        setIsDeleteDialogOpen(false);
                        setSelectedUserIds([]);
                    }}
                />
            )}
        </PageWrapper>
    );
}
